// AI-CONTEXT: See docs/features/supertenant-dashboard-cards.md
// AI-CONTEXT: See docs/features/platform-stats-trend-sparklines.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
import usePlatformStats from '../../hooks/usePlatformStats'
import usePlatformStatsHistory from '../../hooks/usePlatformStatsHistory'
import platformStatDisplay, { PLATFORM_STAT_CARDS } from '../../helpers/dashboard/platformStatDisplay'
import buildPlatformStatSeries from '../../helpers/dashboard/buildPlatformStatSeries'
import toPercentSeries from '../../helpers/dashboard/toPercentSeries'
import formatPercentChange from '../../helpers/formatPercentChange'
import formatMonthLabel from '../../helpers/formatMonthLabel'
import StaticCardLabel from './StaticCardLabel'
import KpiValue from './KpiValue'

/**
 * Compact KPI tile for one platform-wide metric (Deccos management tenant
 * only). One component serves all five platform-stat card types — the metric
 * follows from the DashboardItems Type. Values come from the cached
 * PlatformStats/current doc (nightly computePlatformStats); a missing doc or
 * field shows the placeholder, never a fake 0. While that doc is still on its
 * way the tile shows a loading indicator instead of the placeholder — a dash
 * claims there is no figure, and that is untrue until the read has settled.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * Under the value: the growth since the first month of the month-history
 * (from 2026-07 onward — earlier months are not reconstructable). A single
 * percentage, no trend line; the tile carries the absolute level itself.
 *
 * @param {object} props
 * @param {{ Type: string }} props.item - DashboardItems doc of this card
 */
const DashboardPlatformStatCard = ({ item }) => {
    const { stats, loading } = usePlatformStats()
    const { history } = usePlatformStatsHistory()
    const display = platformStatDisplay(item?.Type, stats)

    if (!display) return null

    // Value and label both come from percentSeries — reading the baseline month
    // from the un-rebased series would decouple them the day that series is
    // trimmed, and a confidently wrong month reads as real.
    const percentSeries = toPercentSeries(
        buildPlatformStatSeries(history, PLATFORM_STAT_CARDS[item?.Type]?.field),
    )
    const change = percentSeries.length ? percentSeries[percentSeries.length - 1] : null
    const since = percentSeries.length ? formatMonthLabel(percentSeries[0].month) : ''

    return (
        <div className='participation-value-card'>
            <StaticCardLabel title={display.label} pill={{ variant: 'msi', label: 'Deccos' }} />
            <KpiValue loading={loading} value={display.value} />
            {display.note && (
                <p className='participation-value-note'>{display.note}</p>
            )}
            {change && (
                <p className={`participation-value-change ${change.value > 0 ? 'pos' : change.value < 0 ? 'neg' : 'neutral'}`}>
                    {formatPercentChange(change.value)}
                    <span className='participation-value-change-since'>{` sinds ${since}`}</span>
                </p>
            )}
        </div>
    )
}

export default DashboardPlatformStatCard
